import { useQuery, useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import type { Environment } from "../../../types";
import type {
  ForgeHolding,
  ForgeHoldingsResponse,
  ForgeTokenDetailResponse,
} from "../types";
import { getApiBase } from "../../../lib/remote-config";

export const FORGE_HOLDINGS_QUERY_KEY = "forge-holdings";
export const FORGE_TOKEN_IMAGE_QUERY_KEY = "forge-token-image";

async function fetchForgeHoldings(
  baseUrl: string,
  walletAddress: string,
  signal?: AbortSignal,
): Promise<ForgeHolding[]> {
  const res = await fetch(
    `${baseUrl}/v1/users/${walletAddress}/holdings`,
    { cache: "no-store", signal },
  );
  if (!res.ok) {
    throw new Error(`Failed to fetch forge holdings: ${res.status}`);
  }
  const json: ForgeHoldingsResponse = await res.json();
  if (!json.success) {
    throw new Error("Forge holdings API error");
  }
  return json.data.items;
}

async function fetchForgeTokenImage(
  baseUrl: string,
  tokenAddress: string,
  signal?: AbortSignal,
): Promise<{ address: string; image_url: string }> {
  const res = await fetch(`${baseUrl}/v1/tokens/${tokenAddress}`, {
    cache: "no-store",
    signal,
  });
  if (!res.ok) {
    return { address: tokenAddress, image_url: "" };
  }
  const json: ForgeTokenDetailResponse = await res.json();
  if (!json.success) {
    return { address: tokenAddress, image_url: "" };
  }
  return {
    address: tokenAddress,
    image_url: json.data.token.image_url,
  };
}

export function useForgeHoldings(
  env: Environment,
  walletAddress: string,
  active = true,
) {
  const { data, ...rest } = useQuery({
    queryKey: [FORGE_HOLDINGS_QUERY_KEY, env, walletAddress],
    queryFn: async ({ signal }) =>
      fetchForgeHoldings(
        await getApiBase("bondingCurve", env),
        walletAddress,
        signal,
      ),
    enabled: !!walletAddress && active,
    staleTime: 0,
    refetchInterval: active ? 5000 : false,
  });

  return { holdings: data ?? [], ...rest };
}

export function useForgeTokenImages(
  env: Environment,
  tokenAddresses: string[],
  active = true,
) {
  const results = useQueries({
    queries: tokenAddresses.map((addr) => ({
      queryKey: [FORGE_TOKEN_IMAGE_QUERY_KEY, env, addr],
      queryFn: async ({ signal }: { signal?: AbortSignal }) =>
        fetchForgeTokenImage(await getApiBase("bondingCurve", env), addr, signal),
      enabled: active,
      staleTime: Infinity,
    })),
  });

  const imageMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of results) {
      if (r.data) {
        map.set(r.data.address, r.data.image_url);
      }
    }
    return map;
  }, [results]);

  return { imageMap };
}
